import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
} from '@merritrac/shared/components';
import { AlertCircle, ClipboardList, ExternalLink, Loader2, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { useGraphQL } from '../hooks/useGraphQL.js';
import { usePortalAuth } from '../hooks/usePortalAuth.js';

import type { ReactElement } from 'react';

/* ── Types ── */

type RequestStatus = 'pending' | 'approved' | 'converted' | 'rejected';
type UrgencyLevel = 'low' | 'normal' | 'urgent';
type FilterStatus = 'All' | RequestStatus;

type GlassBadgeVariant = 'info' | 'success' | 'warning' | 'danger' | 'default';

interface ServiceRequestNode {
  id: string;
  description: string;
  urgency: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  serviceOrderAk: number | null;
  property: { name: string } | null;
  contact: { firstName: string; lastName: string } | null;
}

interface ServiceRequestsData {
  serviceRequests: {
    edges: { node: ServiceRequestNode }[];
  };
}

/* ── GraphQL ── */

const GET_SERVICE_REQUESTS = `
  query GetMyServiceRequests($first: Int) {
    serviceRequests(first: $first) {
      edges {
        node {
          id
          description
          urgency
          status
          createdAt
          updatedAt
          serviceOrderAk
          property { name }
          contact { firstName lastName }
        }
      }
    }
  }
`;

/* ── Status config ── */

function normaliseStatus(raw: string): RequestStatus {
  const lower = raw.toLowerCase();
  if (lower === 'approved') {
    return 'approved';
  }
  if (lower === 'converted') {
    return 'converted';
  }
  if (lower === 'rejected') {
    return 'rejected';
  }
  return 'pending';
}

function normaliseUrgency(raw: string): UrgencyLevel {
  const lower = raw.toLowerCase();
  if (lower === 'urgent') {
    return 'urgent';
  }
  if (lower === 'low') {
    return 'low';
  }
  return 'normal';
}

const statusBadgeVariant: Record<RequestStatus, GlassBadgeVariant> = {
  pending: 'warning',
  approved: 'info',
  converted: 'success',
  rejected: 'danger',
};

const statusLabel: Record<RequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  converted: 'Converted',
  rejected: 'Rejected',
};

const urgencyBadgeVariant: Record<UrgencyLevel, GlassBadgeVariant> = {
  low: 'default',
  normal: 'info',
  urgent: 'danger',
};

const urgencyLabel: Record<UrgencyLevel, string> = {
  low: 'Low',
  normal: 'Normal',
  urgent: 'Urgent',
};

const filterOptions: readonly { value: string; label: string }[] = [
  { value: 'All', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'converted', label: 'Converted' },
  { value: 'rejected', label: 'Rejected' },
];

/* ── Helpers ── */

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/* ── Mobile card ── */

interface RequestCardProps {
  node: ServiceRequestNode;
}

function RequestCard({ node }: RequestCardProps): ReactElement {
  const navigate = useNavigate();
  const status = normaliseStatus(node.status);
  const urgency = normaliseUrgency(node.urgency);

  return (
    <GlassCard
      hover
      className="cursor-pointer"
      onClick={() => {
        void navigate(`/my-requests/${node.id}`);
      }}
    >
      <GlassCardContent className="py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <GlassBadge variant={statusBadgeVariant[status]}>{statusLabel[status]}</GlassBadge>
              <GlassBadge variant={urgencyBadgeVariant[urgency]}>
                {urgencyLabel[urgency]}
              </GlassBadge>
            </div>
            <p className="text-sm text-text font-medium leading-snug mb-1 line-clamp-2">
              {node.description}
            </p>
            {node.property !== null && (
              <p className="text-xs text-text-tertiary">{node.property.name}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-text-tertiary whitespace-nowrap">
              {formatDate(node.createdAt)}
            </p>
            {status === 'converted' && node.serviceOrderAk !== null && (
              <Link
                to={`/orders/${String(node.serviceOrderAk)}`}
                className="text-xs text-primary hover:underline flex items-center gap-0.5 justify-end mt-1"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                View Order
                <ExternalLink size={10} />
              </Link>
            )}
          </div>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

/* ── Page ── */

export function MyRequestsPage(): ReactElement {
  const navigate = useNavigate();
  const user = usePortalAuth((state) => state.user);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('All');

  const { data, loading, error, refetch } = useGraphQL<ServiceRequestsData>(GET_SERVICE_REQUESTS, {
    first: 50,
  });

  const allNodes: ServiceRequestNode[] = data?.serviceRequests.edges.map((e) => e.node) ?? [];

  const filtered = allNodes.filter((node) => {
    const status = normaliseStatus(node.status);
    const matchesStatus = statusFilter === 'All' || status === statusFilter;
    const query = search.toLowerCase();
    const matchesSearch = query === '' || node.description.toLowerCase().includes(query);
    return matchesStatus && matchesSearch;
  });

  function handleView(id: string): void {
    void navigate(`/my-requests/${id}`);
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text">My Requests</h1>
          <p className="text-sm text-text-secondary mt-0.5">
            {loading
              ? 'Loading\u2026'
              : `${String(allNodes.length)} request${allNodes.length !== 1 ? 's' : ''}${user?.clientName !== undefined ? ` for ${user.clientName}` : ''}`}
          </p>
        </div>
        <GlassButton
          variant="primary"
          size="sm"
          onClick={() => {
            void navigate('/my-requests/new');
          }}
        >
          <Plus size={14} />
          New Request
        </GlassButton>
      </div>

      {/* Error banner */}
      {error !== null && (
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">Failed to load requests</p>
            <p className="text-xs mt-0.5 text-danger/80">{error}</p>
          </div>
          <GlassButton variant="ghost" size="sm" onClick={refetch} className="shrink-0 text-danger">
            Retry
          </GlassButton>
        </div>
      )}

      {/* List card */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-text">
              All Requests
              {!loading && (
                <span className="ml-2 text-sm font-normal text-text-secondary">
                  ({String(filtered.length)} shown)
                </span>
              )}
            </h2>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
                />
                <GlassInput
                  placeholder="Search by description..."
                  value={search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearch(e.target.value);
                  }}
                  className="pl-8 w-full sm:w-56"
                  aria-label="Search requests"
                />
              </div>
              <GlassSelect
                options={filterOptions}
                value={statusFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setStatusFilter(e.target.value as FilterStatus);
                }}
                aria-label="Filter by status"
                className="w-full sm:w-40"
              />
            </div>
          </div>
        </GlassCardHeader>

        {/* Loading */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 size={32} className="text-text-tertiary animate-spin" />
            <p className="text-sm text-text-secondary">Loading your requests&hellip;</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <ClipboardList size={40} className="text-text-tertiary" />
            <div className="text-center">
              <p className="text-sm font-medium text-text">No requests found</p>
              <p className="text-xs text-text-secondary mt-1">
                {search !== '' || statusFilter !== 'All'
                  ? 'Try adjusting your search or filter.'
                  : "You haven't submitted any requests yet."}
              </p>
            </div>
            {search === '' && statusFilter === 'All' && (
              <GlassButton
                variant="primary"
                size="sm"
                onClick={() => {
                  void navigate('/my-requests/new');
                }}
              >
                <Plus size={14} />
                Submit a Request
              </GlassButton>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block">
              <GlassTable>
                <GlassTableHead>
                  <GlassTableRow>
                    <GlassTableHeaderCell className="whitespace-nowrap">
                      Request #
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell>Description</GlassTableHeaderCell>
                    <GlassTableHeaderCell className="whitespace-nowrap">
                      Urgency
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell className="whitespace-nowrap">
                      Status
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell className="whitespace-nowrap hidden lg:table-cell">
                      Submitted
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell className="hidden xl:table-cell">
                      Property
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell aria-label="Actions" />
                  </GlassTableRow>
                </GlassTableHead>
                <GlassTableBody>
                  {filtered.map((node) => {
                    const status = normaliseStatus(node.status);
                    const urgency = normaliseUrgency(node.urgency);
                    return (
                      <GlassTableRow
                        key={node.id}
                        className="cursor-pointer"
                        onClick={() => {
                          handleView(node.id);
                        }}
                      >
                        <GlassTableCell>
                          <span className="font-mono text-xs font-semibold text-primary">
                            #{node.id}
                          </span>
                        </GlassTableCell>
                        <GlassTableCell>
                          <p className="text-text leading-snug line-clamp-2">{node.description}</p>
                        </GlassTableCell>
                        <GlassTableCell>
                          <GlassBadge variant={urgencyBadgeVariant[urgency]}>
                            {urgencyLabel[urgency]}
                          </GlassBadge>
                        </GlassTableCell>
                        <GlassTableCell>
                          <GlassBadge variant={statusBadgeVariant[status]}>
                            {statusLabel[status]}
                          </GlassBadge>
                        </GlassTableCell>
                        <GlassTableCell className="text-text-secondary whitespace-nowrap hidden lg:table-cell">
                          {formatDate(node.createdAt)}
                        </GlassTableCell>
                        <GlassTableCell className="text-text-secondary hidden xl:table-cell">
                          <span className="line-clamp-1">{node.property?.name ?? '\u2014'}</span>
                        </GlassTableCell>
                        <GlassTableCell className="text-right">
                          {status === 'converted' && node.serviceOrderAk !== null ? (
                            <Link
                              to={`/orders/${String(node.serviceOrderAk)}`}
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              View Order
                              <ExternalLink size={11} />
                            </Link>
                          ) : (
                            <GlassButton
                              variant="ghost"
                              size="sm"
                              aria-label={`View request #${node.id}`}
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleView(node.id);
                              }}
                            >
                              View
                            </GlassButton>
                          )}
                        </GlassTableCell>
                      </GlassTableRow>
                    );
                  })}
                </GlassTableBody>
              </GlassTable>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden p-4 space-y-3">
              {filtered.map((node) => (
                <RequestCard key={node.id} node={node} />
              ))}
            </div>
          </>
        )}

        {/* Footer count */}
        {!loading && filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-[var(--glass-border-subtle)] flex items-center justify-between">
            <p className="text-xs text-text-tertiary">
              Showing {String(filtered.length)} of {String(allNodes.length)} requests
            </p>
            <GlassBadge variant="default">
              {statusFilter === 'All' ? 'All statuses' : statusLabel[statusFilter]}
            </GlassBadge>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
